import React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import _ from 'lodash';
import {
  Form, Card, CardBody,
} from 'reactstrap';
import { friendlyErrorMessage } from 'app/utils/parsers';
import Alert from 'app/components/general/Alert';
import Button from 'app/components/general/Button';
import InputGroupBlock from 'app/components/general/InputGroupBlock';
import { Link } from 'react-router-dom';

import * as organisationsActions from 'app/ducks/organisations/actions';
import * as authenticationActions from 'app/ducks/authentication/actions';

import * as organisationSelectors from 'app/ducks/organisations/selectors';

import EmailIcon from 'app/images/icons/email.svg';
import OrgIcon from 'app/images/icons/organisation.svg';
import PasswordIcon from 'app/images/icons/password.svg';
import validateInput from './LoginFormValidator';


function selector(state) {
  return {
    organisations: organisationSelectors.getOrganisations(state),
  };
}

const actionCreators = _.merge(organisationsActions, authenticationActions);

interface ISudoLoginFormProps {
  actions?: any;
  email: string;
  password: string;
  isLoading?: boolean;
  hasLoaded?: boolean;
  hasFailed?: boolean;
  isLoggedIn?: boolean;
  changePasswordSuccess: boolean;
  backendErrors: any;
  organisations: any;
}

interface ISudoLoginFormState {
  email?: string;
  password?: string;
  backendErrors?: any;
  errors?: any;
  organisation?: any;
  changed: boolean;
}

@connect(selector, (dispatch) => ({
  actions: bindActionCreators(actionCreators, dispatch),
}))
export default class SudoLoginForm extends React.Component<
  ISudoLoginFormProps,
  ISudoLoginFormState
> {
  private emailInput;

  private passwordInput;

  static defaultProps: ISudoLoginFormProps = {
    email: '',
    password: '',
    isLoading: false,
    backendErrors: {},
    organisations: undefined,
  };

  constructor(props) {
    super(props);
    this.state = {
      email: this.props.email || '',
      password: this.props.password || '',
      backendErrors: this.props.backendErrors || {},
      errors: {},
      organisation: undefined,
      changed: false,
    };

    this.handleChange = this.handleChange.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
    this.emailInput = React.createRef();
    this.passwordInput = React.createRef();
  }

  componentDidMount(): void {
    this.props.actions.resetAuthenticateErrors();

    if (
      this.props.actions.getAllOrganisations
      && !this.props.organisations.isLoading
      && !this.props.organisations.hasLoaded
    ) {
      this.props.actions.getAllOrganisations();
    }
  }

  static getDerivedStateFromProps(nextProps, prevState): any {
    if (!prevState.changed && nextProps.backendErrors !== prevState.backendErrors) {
      return { ...prevState, backendErrors: nextProps.backendErrors };
    }
    return null;
  }

  handleOrganisationChange = (e) => {
    delete this.state.errors.organisation;
    if (e && e.id != null) {
      this.setState({ organisation: e });
    }
    if (e == null) {
      this.setState({ organisation: '' });
    }
  };

  isValid = (): boolean => {
    const { errors, isValid } = validateInput(this.state);

    if (!isValid) {
      this.setState({ errors });
    }

    return isValid;
  };

  handleChange(e): void {
    e.persist();
    this.setState((previousState) => ({
      ...previousState,
      [e.target.name]: e.target.value,
      backendErrors: {},
      errors: {
        ...previousState.errors,
        [e.target.name]: undefined,
      },
      changed: true,
    }));
  }

  handleSubmit = (e): void => {
    e.preventDefault();
    if (this.isValid()) {
      this.props.actions.authenticateCognitoUser({
        email: this.state.email,
        password: this.state.password,
        organisationId: this.state.organisation.id,
      });
    }
  };

  renderBackendErrors = (): React.ReactNode => {
    if (
      !_.isEmpty(this.state.backendErrors?.message)
    ) {
      return (
        <Alert color="error">
          {friendlyErrorMessage(this.state.backendErrors.message)}
        </Alert>
      );
    }
    return null;
  }


  render(): React.ReactNode {
    return (
      <div>
        <div className="row">
          <div className="col">
            <Card className="extra-shadow">
              <CardBody className="with-extra-padding-2">
                <div className="row">
                  <div className="col">
                    <h2>Sign in to an organisation</h2>
                  </div>
                </div>
                {this.props.changePasswordSuccess && (
                  <div className="row">
                    <div className="col">
                      <Alert color="success">Password changed successfully, please login with your new password.</Alert>
                    </div>
                  </div>
                )}
                {this.renderBackendErrors()}
                <div className="row with-margin-top">
                  <div className="col">
                    <Form autoComplete="off" onSubmit={this.handleSubmit}>
                      <InputGroupBlock
                        type="text"
                        icon={EmailIcon}
                        labelText="Your email"
                        placeholder="Your email"
                        aria-label="Your email"
                        errors={this.state.errors}
                        autoComplete="on"
                        inputGroupClassName="large"
                        tabIndex="1"
                        name="email"
                        value={this.state.email}
                        innerRef={this.emailInput}
                        onChange={this.handleChange}
                        onBlur={this.handleChange}
                      />
                      <InputGroupBlock
                        type="password"
                        icon={PasswordIcon}
                        labelText="Password"
                        placeholder="Password"
                        aria-label="Password"
                        errors={this.state.errors}
                        autoComplete="on"
                        inputGroupClassName="large"
                        hint={(
                          <Link to="/forgot-password">
                            Forgot your password?
                          </Link>
                        )}
                        tabIndex="2"
                        name="password"
                        innerRef={this.passwordInput}
                        value={this.state.password}
                        onChange={this.handleChange}
                        onBlur={this.handleChange}

                      />
                      <InputGroupBlock
                        type="select"
                        icon={OrgIcon}
                        labelText="Organisation"
                        name="organisation"
                        inputGroupClassName="large height-50"
                        placeholder="Sports organisation/league"
                        aria-label="Sports organisation/league"
                        autoComplete="off"
                        tabIndex="3"
                        clearable
                        errors={this.state.errors}
                        value={this.state.organisation}
                        options={
                          this.props.organisations.organisationsByIdInSelectFormat
                        }
                        onChange={this.handleOrganisationChange}
                      />
                      <Button
                        color="primary"
                        className="btn-1 btn-full-width success-sm large"
                        tabIndex="3"
                        delaySubmit
                        isLoading={this.props.isLoading}
                        hasSaved={this.props.hasLoaded}
                        hasFailed={this.props.hasFailed}
                        type="submit"
                      >
                        Log in
                      </Button>
                    </Form>
                  </div>
                </div>
              </CardBody>
            </Card>
          </div>
        </div>
        <div className="row justify-content-center sign-up">
          <div className="col text-center with-margin-bottom-10">
            <span className="d-inline-block text-grey">Don't have an account?</span>
            {' '}
            <Link to="/get-started">Sign up</Link>
          </div>
        </div>
      </div>
    );
  }
}
